import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function flattenOptions(options) {
  const flat = [];
  options.forEach((entry) => {
    if (entry && Array.isArray(entry.options)) {
      entry.options.forEach((opt) => flat.push(opt));
    } else if (entry) {
      flat.push(entry);
    }
  });
  return flat;
}

export default function AppSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  selectedPrefix = '',
  style,
  active = false,
  className = '',
  disabled = false,
}) {
  const safeStyle = { ...(style || {}) };
  delete safeStyle.padding;
  delete safeStyle.minHeight;
  delete safeStyle.fontSize;
  delete safeStyle.lineHeight;

  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const flatOptions = useMemo(() => flattenOptions(options), [options]);
  const selected = flatOptions.find((opt) => String(opt.value) === String(value));
  const hasPrefixedSelection = Boolean(selectedPrefix && selected);

  const updateMenuPosition = React.useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 220 && rect.top > 260;
    setMenuPos({
      left: rect.left,
      width: rect.width,
      top: openUp ? rect.top - 6 : rect.bottom + 6,
      openUp,
    });
  }, []);

  useEffect(() => {
    const handleDown = (event) => {
      const target = event.target;
      const clickedInTrigger = rootRef.current && rootRef.current.contains(target);
      const clickedInMenu = menuRef.current && menuRef.current.contains(target);
      if (!clickedInTrigger && !clickedInMenu) setOpen(false);
    };
    document.addEventListener('mousedown', handleDown);
    return () => document.removeEventListener('mousedown', handleDown);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onWindowChange = () => updateMenuPosition();
    window.addEventListener('resize', onWindowChange);
    window.addEventListener('scroll', onWindowChange, true);
    return () => {
      window.removeEventListener('resize', onWindowChange);
      window.removeEventListener('scroll', onWindowChange, true);
    };
  }, [open, updateMenuPosition]);

  return (
    <div
      ref={rootRef}
      className={className}
      style={{ position: 'relative', width: '100%' }}
    >
      <button
        className="app-select-trigger"
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        disabled={disabled}
        style={{
          width: '100%',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          minHeight: 'var(--ui-select-height, 28px)',
          padding: `0 var(--ui-select-pad-x, 10px)`,
          borderRadius: '12px',
          border: active
            ? '1px solid color-mix(in srgb, var(--text-link, #228be6) 62%, var(--card-border) 38%)'
            : '1px solid var(--card-border)',
          background: active
            ? 'color-mix(in srgb, var(--text-link, #228be6) 14%, var(--input-bg) 86%)'
            : 'var(--input-bg)',
          color: 'var(--text-primary)',
          fontSize: 'var(--ui-select-font, var(--ui-compact-font))',
          lineHeight: 1.2,
          boxShadow: active ? '0 1px 4px color-mix(in srgb, var(--text-link, #228be6) 18%, transparent)' : 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.58 : 1,
          ...safeStyle,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {hasPrefixedSelection ? (
            <>
              <span>{selectedPrefix}: </span>
              <span style={{ fontWeight: 700 }}>{selected.label}</span>
            </>
          ) : selected ? (
            selected.label
          ) : selectedPrefix ? (
            `${selectedPrefix}: ${placeholder || '—'}`
          ) : (
            placeholder
          )}
        </span>
        <span style={{ opacity: disabled ? 0.5 : 0.75, fontSize: '0.62rem', lineHeight: 1 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && !disabled && menuPos && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            left: `${menuPos.left}px`,
            width: `${menuPos.width}px`,
            top: `${menuPos.top}px`,
            transform: menuPos.openUp ? 'translateY(-100%)' : 'none',
            zIndex: 1400,
            maxHeight: '260px',
            overflowY: 'auto',
            background: 'color-mix(in srgb, var(--bg-card) 92%, #0b2238 8%)',
            border: '1px solid color-mix(in srgb, var(--text-link, #228be6) 28%, var(--card-border) 72%)',
            borderRadius: '12px',
            boxShadow: '0 10px 24px rgba(0,0,0,0.28)',
            padding: '4px',
          }}
        >
          {options.map((entry, i) => {
            if (entry && Array.isArray(entry.options)) {
              return (
                <div key={`grp-${entry.label}-${i}`} style={{ marginBottom: '2px' }}>
                  <div
                    style={{
                      fontSize: '0.7rem',
                      fontWeight: 800,
                      opacity: 0.92,
                      padding: '5px 8px 4px',
                      marginTop: i === 0 ? 0 : '3px',
                      marginBottom: '3px',
                      borderTop: i === 0 ? 'none' : '1px solid color-mix(in srgb, var(--card-border) 70%, transparent)',
                      color: 'color-mix(in srgb, var(--text-muted) 84%, var(--text-primary) 16%)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {entry.label}
                  </div>
                  {entry.options.map((opt) => {
                    const isSelected = String(opt.value) === String(value);
                    return (
                      <button
                        key={`${entry.label}-${opt.value}`}
                        type="button"
                        onClick={() => {
                          onChange(opt.value);
                          setOpen(false);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          border: 'none',
                          borderRadius: '8px',
                          padding: 'var(--ui-select-option-pad-y, 6px) var(--ui-select-option-pad-x, 11px)',
                          fontSize: 'var(--ui-select-font, var(--ui-compact-font))',
                          background: isSelected
                            ? 'color-mix(in srgb, var(--text-link, #228be6) 24%, transparent)'
                            : 'transparent',
                          color: 'var(--text-primary)',
                          cursor: 'pointer',
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              );
            }

            const isSelected = String(entry.value) === String(value);
            return (
              <button
                key={`opt-${entry.value}-${i}`}
                type="button"
                onClick={() => {
                  onChange(entry.value);
                  setOpen(false);
                }}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  borderRadius: '8px',
                  padding: 'var(--ui-select-option-pad-y, 6px) var(--ui-select-option-pad-x, 11px)',
                  fontSize: 'var(--ui-select-font, var(--ui-compact-font))',
                  background: isSelected
                    ? 'color-mix(in srgb, var(--text-link, #228be6) 24%, transparent)'
                    : 'transparent',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                }}
              >
                {entry.label}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
